import { useState } from 'react';
import {
  Box,
  Button,
  InlineStack,
  Text,
  TextField,
} from '@shopify/polaris';
import { EditIcon } from '@shopify/polaris-icons';
import { t } from '../i18n';

interface CampaignNameEditorProps {
  name: string;
  onChange: (name: string) => void;
  placeholder?: string;
  /** When true, start in edit mode (e.g. empty name on create) */
  defaultEditing?: boolean;
}

/**
 * Inline campaign name field with pencil toggle.
 * Place under Page title on all campaign create/edit wizards.
 */
export function CampaignNameEditor({
  name,
  onChange,
  placeholder = 'Campaign name',
  defaultEditing = false,
}: CampaignNameEditorProps) {
  const [editing, setEditing] = useState(defaultEditing || !name.trim());

  if (editing) {
    return (
      <Box
        padding="300"
        background="bg-surface-secondary"
        borderRadius="200"
        borderWidth="025"
        borderColor="border"
      >
        <InlineStack gap="200" blockAlign="end" wrap={false}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <TextField
              label={t("Campaign name")}
              value={name}
              onChange={onChange}
              autoComplete="off"
              placeholder={placeholder}
              autoFocus
              helpText={t("This name is shown in your campaigns list.")}
            />
          </div>
          <Button
            variant="primary"
            onClick={() => setEditing(false)}
            disabled={!name.trim()}
          >
            {t("Done")}
          </Button>
        </InlineStack>
      </Box>
    );
  }

  return (
    <Box
      padding="300"
      background="bg-surface-secondary"
      borderRadius="200"
      borderWidth="025"
      borderColor="border"
    >
      <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
        <BlockLabel name={name} placeholder={placeholder} />
        <Button
          icon={EditIcon}
          onClick={() => setEditing(true)}
          accessibilityLabel={t("Edit campaign name")}
        >
          {t("Edit name")}
        </Button>
      </InlineStack>
    </Box>
  );
}

function BlockLabel({
  name,
  placeholder,
}: {
  name: string;
  placeholder: string;
}) {
  const hasName = Boolean(name.trim());
  return (
    <InlineStack gap="200" blockAlign="center" wrap>
      <Text as="span" tone="subdued" variant="bodySm">
        {t("Campaign name")}
      </Text>
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {hasName ? name.trim() : placeholder}
      </Text>
      {!hasName && (
        <Text as="span" tone="subdued" variant="bodySm">
          {t("(click Edit name)")}
        </Text>
      )}
    </InlineStack>
  );
}

/** Static page title for create/edit + campaign type. */
export function campaignPageLabel(isEdit: boolean, typeLabel: string): string {
  return isEdit
    ? t('Edit campaign · {{type}}', { type: typeLabel })
    : t('Create campaign · {{type}}', { type: typeLabel });
}
